import { ShieldCheck, Handshake, Boxes } from "lucide-react";
import { SiteHeader } from "../../components/site/SiteHeader";
import { SiteFooter } from "../../components/site/SiteFooter";
import { PageHero } from "../../components/site/PageHero";
import { CTABand } from "../../components/site/CTABand";
import { Reveal, Stagger, StaggerItem } from "../../components/motion/Reveal";
import { TECH_CATEGORIES, BRAND_RELATIONSHIPS, SELECTION_CRITERIA } from "../../data/technology";

const REL_ICONS = { gold: ShieldCheck, mid: Handshake, dim: Boxes };
const REL_STYLES = {
  gold: "border-nx-gold/50 text-nx-goldlight",
  mid: "border-sky-500/40 text-sky-300",
  dim: "border-white/15 text-slate-400",
};

export default function Technology() {
  return (
    <div className="font-publicsans bg-nx-navy text-slate-200 antialiased" data-testid="technology-page">
      <SiteHeader />
      <PageHero
        crumb="Technology"
        eyebrow="Technology Ecosystem"
        title="We engineer the solution."
        titleAccent="Technology follows."
        description="NexSecure is not a product reseller. Every project starts with your requirement — technology is selected for the job, from manufacturers we know and trust."
      />
      <section className="py-16 sm:py-24 border-b border-white/10">
        <div className="nx-wrap">
          <Reveal>
            <h2 className="font-marcellus text-3xl sm:text-4xl text-white mb-4">Selection philosophy</h2>
            <p className="text-base md:text-lg text-slate-400 max-w-2xl leading-relaxed mb-10">Every recommendation weighs the same eight criteria — never brand preference, never stock to clear.</p>
          </Reveal>
          <Stagger className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {SELECTION_CRITERIA.map((c, i) => (
              <StaggerItem key={c}>
                <div className="bg-nx-navy p-6 flex items-center gap-4" data-testid={`tech-criterion-${i}`}>
                  <span className="font-marcellus text-nx-gold/60">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-sm text-slate-200">{c}</span>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>
      <section className="py-16 sm:py-24 border-b border-white/10 bg-nx-navy2">
        <div className="nx-wrap">
          <Reveal>
            <span className="text-[11px] tracking-[0.3em] uppercase text-nx-goldlight">Ecosystem</span>
            <h2 className="font-marcellus text-3xl sm:text-4xl text-white mt-4 mb-12">Technology categories we work across</h2>
          </Reveal>
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {TECH_CATEGORIES.map((c, i) => (
              <StaggerItem key={c.name}>
                <div className="h-full bg-nx-navy2 hover:bg-nx-navy p-7 transition-colors" data-testid={`tech-category-${i}`}>
                  <h3 className="font-marcellus text-lg text-white mb-3">{c.name}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{c.text}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>
      <section className="py-16 sm:py-24">
        <div className="nx-wrap">
          <Reveal>
            <span className="text-[11px] tracking-[0.3em] uppercase text-nx-goldlight">Manufacturer Relationships</span>
            <h2 className="font-marcellus text-3xl sm:text-4xl text-white mt-4 mb-4">How we present brands — honestly.</h2>
            <p className="text-base text-slate-400 max-w-2xl leading-relaxed mb-12">We work with manufacturers including TP-Link/Omada, Vertiv and Hikvision, alongside networking, cabling, power and server vendors. Every brand we name carries one of three clearly-stated relationships:</p>
          </Reveal>
          <Stagger className="grid sm:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {BRAND_RELATIONSHIPS.map((r, i) => {
              const Icon = REL_ICONS[r.tone];
              return (
                <StaggerItem key={r.label}>
                  <div className="h-full bg-nx-navy p-8" data-testid={`tech-relationship-${i}`}>
                    <Icon size={24} className={`${r.tone === "gold" ? "text-nx-goldlight" : r.tone === "mid" ? "text-sky-300" : "text-slate-400"} mb-5`} strokeWidth={1.5} />
                    <span className={`inline-block text-[10px] tracking-[0.2em] uppercase border px-3 py-1.5 mb-4 ${REL_STYLES[r.tone]}`}>{r.label}</span>
                    <p className="text-sm text-slate-400 leading-relaxed">{r.text}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </Stagger>
          <Reveal delay={0.15}>
            <p className="mt-10 text-xs text-slate-500 leading-relaxed max-w-3xl" data-testid="tech-disclaimer">
              Product availability, specifications and manufacturer relationships may change over time. All technology recommendations are based on project requirements and site conditions, confirmed at design stage.
            </p>
          </Reveal>
        </div>
      </section>
      <CTABand title="Not sure which technology fits?" text="That's the point of a site assessment — we specify from your requirement, not from a catalogue." />
      <SiteFooter />
    </div>
  );
}
